/**
 * Converts a ValidationError into an error object with a message, name, and type.
 * @param {ValidationError} validationError The validation error to convert into an error object
 * @returns An object with a message, name, and type
 */
function formatValidationError({ errors, path, type }) {
  return {
    message: errors?.[0],
    name: path,
    type,
  };
}

/**
 * Takes an object with search params and filters out the ones that produced validation errors.
 * @param {object} searchParams The search params to filter for validation
 * @param {array} errors The errors produced during validation
 * @returns An object with the validated search params
 */
function filterValidatedSearchParams(searchParams, errors = []) {
  const results = Object.entries(searchParams).reduce((filteredParams, [name, value]) => {
    if (!paramContainsErrors([name, value], errors)) {
      filteredParams[name] = value;
    }

    return filteredParams;
  }, {});

  return results;
}

function paramContainsErrors([name, value], errors = []) {
  let namesToFind = [name];

  if (typeof value === "object") {
    namesToFind = Object.keys(value).map((innerParamName) => `${name}.${innerParamName}`);
  }

  return errors.some((error) => namesToFind.includes(error.name));
}

/**
 * Validates a set of search params against the defined yup validations.
 * @param {object} searchParams The search parameters to validate
 * @param {ObjectSchema} validations The validations to perform on the search params
 * @returns An object with the errors and valid search params
 */
export function validateSearchParams(searchParams, validations) {
  let results = { errors: [], searchParams };

  try {
    validations.validateSync(searchParams, { abortEarly: false });
  } catch (validationErrors) {
    const formattedErrors = validationErrors.inner.map(formatValidationError);

    results = {
      errors: formattedErrors,
      searchParams: filterValidatedSearchParams(searchParams, formattedErrors),
    };
  }

  return results;
}
